import React from 'react';
import PropTypes from 'prop-types';

import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container';
import useStyles from './CreateSaml.styled';
import * as Yup from 'yup';
import i18n from 'i18n-js';
import { Formik, Field } from 'formik';
import { TextField, TextareaField, SwitchField } from 'components/FormFields';
import InfoIcon from '@material-ui/icons/Info';
import { Tooltip } from '@material-ui/core';
import { AuthActions } from 'state/modules/auth';
import { useDispatch } from 'react-redux';
import Typography from '@material-ui/core/Typography';

const InfoTooltip = ({ title, className }) => {
  return (
    <Tooltip title={title} className={className}>
      <InfoIcon color="primary" />
    </Tooltip>
  );
};

const CreateSaml = ({ formRef, errors, step }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const formSchema = Yup.object().shape({
    idp_endpoint: Yup.string().required(i18n.t('common.required_field')),
    domains: Yup.string().required(i18n.t('common.required_field')),
    enabled: Yup.boolean(),
  });

  const initialValues = {
    idp_endpoint: step?.idp_endpoint || '',
    enabled: step?.enabled || false,
    domains: step?.domains?.join('\n') || '',
  };

  const onSubmit = (values) => {
    const payload = {
      ...values,
    };
    payload['domains'] = values['domains'].split(/[\n,]/).map((domain) => domain.trim());
    dispatch(AuthActions.fetchUpsertIdentityProvider(payload, 'details'));
  };

  return (
    <Box className={classes.container}>
      <Container className="h-100">
        <Grid
          container
          className={classes.container}
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={12}>
            <Typography color="textPrimary" component="div" align="left">
              <Box fontSize={16} component="h5" lineHeight={1}>
                {i18n.t('authentication.wizard.edit_saml')}
              </Box>
            </Typography>
            <Typography
              color="textPrimary"
              component="div"
              align="justify"
              className={classes.description}
            >
              <Box fontSize={14} mt={2} mb={4} lineHeight={1} component="div">
                <span
                  dangerouslySetInnerHTML={{
                    __html: i18n.t('authentication.wizard.edit_saml_description'),
                  }}
                />
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={12} sm={10}>
            <Formik
              innerRef={formRef}
              initialValues={initialValues}
              validationSchema={formSchema}
              enableReinitialize={true}
              onSubmit={onSubmit}
            >
              {({ handleSubmit }) => (
                <form noValidate onSubmit={handleSubmit}>
                  <Grid
                    container
                    spacing={4}
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                  >
                    <Grid item xs={3} className={classes.textContainer}>
                      <b>{i18n.t('authentication.wizard.idp_endpoint')}</b>
                      <InfoTooltip
                        title={i18n.t('authentication.wizard.tooltip.idp_endpoint')}
                        disabled={false}
                        className={classes.tooltipSize}
                      />
                    </Grid>
                    <Grid item xs={9}>
                      <Field
                        type="text"
                        name="idp_endpoint"
                        variant="outlined"
                        size="small"
                        placeholder={i18n.t('authentication.wizard.placeholder.idp_endpoint')}
                        component={TextField}
                        asyncErrors={errors?.idp_endpoint}
                      />
                    </Grid>
                    <Grid item xs={3} className={classes.textContainer}>
                      <b>{i18n.t('authentication.wizard.domains')}</b>
                      <InfoTooltip
                        title={i18n.t('authentication.wizard.tooltip.domains')}
                        disabled={false}
                        className={classes.tooltipSize}
                      />
                    </Grid>
                    <Grid item xs={9}>
                      <Field
                        rows={2}
                        type="text"
                        name="domains"
                        placeholder={i18n.t('authentication.wizard.placeholder.domains')}
                        required={true}
                        variant="outlined"
                        component={TextareaField}
                        className={classes.textAreaStyles}
                        asyncErrors={errors?.domains}
                      />
                    </Grid>
                    {step?.has_certificate && (
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="left">
                          <Field
                            name="enabled"
                            color="primary"
                            component={SwitchField}
                            asyncErrors={errors?.enabled}
                          />
                          <b style={{ marginLeft: 20 }}>
                            {i18n.t('authentication.wizard.turn_on_off')}
                          </b>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

CreateSaml.propTypes = {
  formRef: PropTypes.object,
  errors: PropTypes.object,
  step: PropTypes.shape({
    id: PropTypes.string,
    idp_endpoint: PropTypes.string,
    enabled: PropTypes.bool,
    has_certificate: PropTypes.bool,
    domains: PropTypes.arrayOf(PropTypes.string),
  }),
  exit: PropTypes.func,
};

InfoTooltip.propTypes = {
  title: PropTypes.string.isRequired,
  className: PropTypes.string,
};

export default CreateSaml;
